import { KeyValue } from "../../../models/KeyValue";
import { assertNumber, isNumber, assertTrue } from "../../utils/assert";
import { Store } from "../../../flux/Store";
import { Settings } from "../../../rest/settings";
import { Unit, Measures } from "../../../rest/Misc";
import { names } from "./SettingsActions";
import { Division } from "../../../models";
import { LanguageOption } from "../../../polyglot/languagePacks";

export interface SettingsStoreState
{
    division: Division;
    
    quantities: QuantityWithUnits[];
    quantitySettings: { [key: number]: number };

    emails: string;

    selectedLanguage: LanguageOption;
    languages: LanguageOption[];
    passwordFlag: boolean;
    password: string;
    passwordConfirmation: string;

    dontAlertLeavingRedZone: boolean;
    dontAlertEnteringYellowZone: boolean;
    dontAlertLeavingYellowZone : boolean;
}

export interface QuantityWithUnits
{
    Id: number;
    Name: string;
    RelativeLevels: boolean;
    IsUnitEditable: boolean;
    Units: Unit[];
    SelectedUnitId?: number;
}

// This is a storage of settings data
// It receives actions and transfors the state accordingly
// This is a Store in temrs of Flux architecture
export class SettingsStore extends Store<SettingsStoreState>
{
    constructor()
    {
        super();
        this.state = this.getDefaultState();

        this.register<Division>(names.SET_DIVISION, e => this.field({ division: e.payload }));
        this.register<Settings>(names.SET_SETTINGS_ITEMS, e => this.setSettingsItems(e.payload));
        this.register<KeyValue<number, number>>(names.SET_QUANTITY_UNIT, e => this.setQuantity(e.payload));
        this.register<string>(names.SET_EMAILS, e => this.setEmails(e.payload));
        this.register<LanguageOption[]>(names.SET_LANGUAGE_ITEMS, e => this.field({ languages: e.payload}));
        this.register<LanguageOption>(names.SET_LANGUAGE, e => this.field({ selectedLanguage: e.payload }));
        this.register<boolean>(names.SET_PASSWORD_FLAG, e => this.field({ passwordFlag: e.payload }));
        this.register<string>(names.SET_PASSWORD, e => this.field({ password: e.payload }));
        this.register<string>(names.SET_PASSWORD_CONFIRMATION, e => this.field({ passwordConfirmation: e.payload }));
        this.register<boolean>(names.SET_DONT_ALERT_LEAVING_RED, e => this.field({ dontAlertLeavingRedZone: e.payload}));
        this.register<boolean>(names.SET_DONT_ALERT_ENTERING_YELLOW, e => this.field({ dontAlertEnteringYellowZone: e.payload}));
        this.register<boolean>(names.SET_DONT_ALERT_LEAVING_YELLOW, e => this.field({ dontAlertLeavingYellowZone: e.payload}));     
        this.register(names.REJECT, e => this.getDefaultState());
    }

    setSettingsItems(settings: Settings)
    {
        const quantities: QuantityWithUnits[] = populateUnits(settings.Measures);
        const quantitySettings = quantities.reduce((obj, q) => {
            obj[q.Id] = q.SelectedUnitId;
            return obj;
        }, {});
        return this.field({ quantities, quantitySettings, emails: settings.EmailAddresses , dontAlertLeavingRedZone: settings.DontAlertLeavingRed, dontAlertEnteringYellowZone: settings.DontAlertEnteringYellow, dontAlertLeavingYellowZone : settings.DontAlertLeavingYellow });
    }

    setQuantity(pair: KeyValue<number, number>)
    {
        assertNumber(pair.key, 'key has to be a number');
        assertTrue(pair.value === null || isNumber(pair.value), 'value has to be a number');
        
        const qSettings = this.state.quantitySettings;
        return this.field({ quantitySettings: {...qSettings, [pair.key]: pair.value} });
    }

    setEmails(emails: string)
    {
        return this.field({ emails });
    }

    private getDefaultState = (): SettingsStoreState => ({
        division: null,
        quantities: [],
        quantitySettings: {},
        emails: '',
        
        languages: [],
        selectedLanguage: null,
        passwordFlag: false,
        password: '',
        passwordConfirmation: '',        
        dontAlertLeavingRedZone: false,
        dontAlertEnteringYellowZone: false,
        dontAlertLeavingYellowZone : false
    });
}

function populateUnits(measures: Measures): QuantityWithUnits[]
{
    type map = {[key: string]: Unit};

    const quantities = measures.Quantities;
    const units = measures.Units;

    const map = units.reduce((obj: map, u: Unit) => {
        obj[u.Id] = u;
        return obj;
    }, {});

    const result = quantities.map(q => ({ ...q, Units: q.Units.map(u => map[u]) }));
    return result;
}



// WEBPACK FOOTER //
// ./src/app/components/Settings/SettingsStore.ts